import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { LockClosedIcon, KeyIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import { auth } from '../../firebase';
import { EmailAuthProvider, reauthenticateWithCredential, updatePassword } from 'firebase/auth';
import ConfirmDialog from '../common/ConfirmDialog';
import { motion } from 'framer-motion';

interface ChangePasswordDialogProps {
    open: boolean;
    onClose: () => void;
}

const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({ open, onClose }) => {
    const { t } = useTranslation();
    const [oldPassword, setOldPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState(false);

    const handleSubmit = async () => {
        if (newPassword !== confirmPassword) {
            setError(t('profile.changePassword.errors.passwordMismatch'));
            return;
        }

        setLoading(true);
        setError('');

        try {
            const user = auth.currentUser;
            if (!user?.email) throw new Error('No user email');

            const credential = EmailAuthProvider.credential(user.email, oldPassword);
            await reauthenticateWithCredential(user, credential);
            await updatePassword(user, newPassword);
            
            setShowSuccessDialog(true);
            setOldPassword('');
            setNewPassword('');
            setConfirmPassword('');
            onClose();
        } catch (error: any) {
            switch (error.code) {
                case 'auth/wrong-password':
                    setError(t('profile.changePassword.errors.wrongPassword'));
                    break;
                case 'auth/weak-password':
                    setError(t('profile.changePassword.errors.weakPassword'));
                    break;
                default:
                    setError(t('profile.changePassword.errors.default'));
                    console.error('Change password error:', error);
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Transition.Root show={open} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={onClose}>
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-indigo-900/70 backdrop-blur-sm transition-opacity" />
                    </Transition.Child>

                    <div className="fixed inset-0 z-10 overflow-y-auto">
                        <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                                enterTo="opacity-100 translate-y-0 sm:scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            >
                                <Dialog.Panel className="relative transform overflow-hidden rounded-2xl bg-white/10 backdrop-blur-xl px-4 pb-4 pt-5 text-left shadow-2xl transition-all border border-white/20 sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
                                    <div>
                                        <motion.div 
                                            className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-blue-500/30 border border-blue-300/50 shadow-lg"
                                            initial={{ scale: 0.8, opacity: 0 }}
                                            animate={{ scale: 1, opacity: 1 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <ShieldCheckIcon className="h-8 w-8 text-blue-100" aria-hidden="true" />
                                        </motion.div>
                                        <motion.div 
                                            className="mt-4 text-center"
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ duration: 0.4 }}
                                        >
                                            <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-white">
                                                {t('profile.changePassword.title')}
                                            </Dialog.Title>
                                            <div className="mt-6 space-y-5">
                                                {/* 当前密码 */}
                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.3, delay: 0.1 }}
                                                >
                                                    <div className="relative rounded-lg shadow-lg">
                                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                            <KeyIcon className="h-5 w-5 text-blue-300" aria-hidden="true" />
                                                        </div>
                                                        <input
                                                            type="password"
                                                            value={oldPassword}
                                                            onChange={(e) => {
                                                                setOldPassword(e.target.value);
                                                                setError('');
                                                            }}
                                                            className={`block w-full rounded-lg border-0 py-3 pl-10 text-white bg-white/10 backdrop-blur-sm ring-1 ring-inset ${
                                                                error 
                                                                    ? 'ring-red-400 placeholder:text-red-200 focus:ring-red-400' 
                                                                    : 'ring-blue-300/50 placeholder:text-blue-200/70 focus:ring-blue-300'
                                                            } focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6`}
                                                            placeholder={t('profile.changePassword.oldPassword')}
                                                        />
                                                    </div>
                                                </motion.div>

                                                {/* 新密码 */}
                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.3, delay: 0.2 }}
                                                >
                                                    <div className="relative rounded-lg shadow-lg">
                                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                            <LockClosedIcon className="h-5 w-5 text-blue-300" aria-hidden="true" />
                                                        </div>
                                                        <input
                                                            type="password"
                                                            value={newPassword}
                                                            onChange={(e) => {
                                                                setNewPassword(e.target.value);
                                                                setError('');
                                                            }}
                                                            className={`block w-full rounded-lg border-0 py-3 pl-10 text-white bg-white/10 backdrop-blur-sm ring-1 ring-inset ${
                                                                error 
                                                                    ? 'ring-red-400 placeholder:text-red-200 focus:ring-red-400' 
                                                                    : 'ring-blue-300/50 placeholder:text-blue-200/70 focus:ring-blue-300'
                                                            } focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6`}
                                                            placeholder={t('profile.changePassword.newPassword')}
                                                        />
                                                    </div>
                                                </motion.div>

                                                {/* 确认新密码 */}
                                                <motion.div
                                                    initial={{ opacity: 0, y: 20 }}
                                                    animate={{ opacity: 1, y: 0 }}
                                                    transition={{ duration: 0.3, delay: 0.3 }}
                                                >
                                                    <div className="relative rounded-lg shadow-lg">
                                                        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                                                            <ShieldCheckIcon className="h-5 w-5 text-blue-300" aria-hidden="true" />
                                                        </div>
                                                        <input
                                                            type="password"
                                                            value={confirmPassword}
                                                            onChange={(e) => {
                                                                setConfirmPassword(e.target.value);
                                                                setError('');
                                                            }}
                                                            className={`block w-full rounded-lg border-0 py-3 pl-10 text-white bg-white/10 backdrop-blur-sm ring-1 ring-inset ${
                                                                error 
                                                                    ? 'ring-red-400 placeholder:text-red-200 focus:ring-red-400' 
                                                                    : 'ring-blue-300/50 placeholder:text-blue-200/70 focus:ring-blue-300'
                                                            } focus:ring-2 focus:ring-inset sm:text-sm sm:leading-6`}
                                                            placeholder={t('profile.changePassword.confirmPassword')}
                                                        />
                                                    </div>
                                                </motion.div>

                                                {error && (
                                                    <motion.p 
                                                        className="mt-3 text-sm text-red-200"
                                                        initial={{ opacity: 0, y: -10 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        transition={{ duration: 0.2 }}
                                                    >
                                                        {error}
                                                    </motion.p>
                                                )}
                                            </div>
                                        </motion.div>
                                    </div>
                                    <div className="mt-8 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-4">
                                        <button
                                            type="button"
                                            className="inline-flex w-full justify-center rounded-full bg-blue-500 bg-opacity-80 px-4 py-2.5 text-sm font-medium text-white shadow-lg hover:bg-opacity-100 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:ring-offset-2 focus:ring-offset-blue-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 sm:col-start-2"
                                            onClick={handleSubmit}
                                            disabled={loading}
                                        >
                                            {loading ? t('common.saving') : t('common.confirm')}
                                        </button>
                                        <button
                                            type="button"
                                            className="mt-3 inline-flex w-full justify-center rounded-full bg-white/10 px-4 py-2.5 text-sm font-medium text-white backdrop-blur-sm shadow-lg ring-1 ring-inset ring-white/20 hover:bg-white/20 transition-all duration-200 sm:col-start-1 sm:mt-0"
                                            onClick={onClose}
                                            disabled={loading}
                                        >
                                            {t('common.cancel')}
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition.Root>

            {/* 成功提示对话框 */}
            <ConfirmDialog
                open={showSuccessDialog}
                title={t('common.success')}
                message={t('profile.changePassword.success')}
                confirmText={t('common.confirm')}
                onConfirm={() => setShowSuccessDialog(false)}
                onCancel={() => setShowSuccessDialog(false)}
                type="info"
            />
        </>
    );
};

export default ChangePasswordDialog; 